// Accounting › Payments and refunds (Blueprint's alone; pages/ux-full accounting/payments), on ui-web's
// ListPage (patterns/README.md, BL-189): every payment a page at a time (payments.list), by where it
// stands, the day it came in, or a customer, reference or invoice; what the filter adds up to in the
// title block's figures. A row opens the payment on its customer's Money tab, where a refund starts.
import { DataTable, Figures, money, paymentColumns, TextField } from '@flowology/ui-web/fl';
import type { PaymentListProps } from '@flowology/ui-web';
import { ListPage } from '@flowology/ui-web/patterns';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { docHref } from '../shared/doc-link.ts';
import { listStatus, moreRows, useListFilter, useMore } from './money-list-state.ts';
import { listInput, PAYMENT_SHOWS, rangeWrong, type PaymentShow } from './money-lists-model.ts';
import { BOOKS_PARTS } from './parts.ts';
import { useQuery } from './ui.tsx';

const invoiceHref = (id: string) => docHref({ kind: 'invoice', id });
const paymentHref = (id: string) => docHref({ kind: 'payment', id });
const SHOWS = PAYMENT_SHOWS.map(([id, label]) => ({ id, label }));
const PART = BOOKS_PARTS.find((p) => p.part === 'payments')!;
const WRONG_DAYS = 'The first day is after the last.';

export function PaymentsPage({ nav, timeZone }: { nav: NavProps; timeZone?: string }) {
  const [f, set, typed, type] = useListFilter<PaymentShow>();
  const wrong = rangeWrong(f);
  const q = useQuery<PaymentListProps>('payments.list', wrong ? null : listInput(f));
  const more = useMore<PaymentListProps['rows'][number]>('payments.list', q.data, (after) =>
    listInput(f, after),
  );
  const t = q.data?.totals;
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <ListPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: PART.bp ?? PART.rail,
            lede: PART.lede,
            ...(t
              ? {
                  figures: (
                    <Figures
                      items={[
                        {
                          label: 'Payments',
                          value: String(t.count),
                          sub: f.show === 'all' && !f.search ? 'every one' : 'in this filter',
                        },
                        { label: 'Came in', value: money(t.inCents) },
                        { label: 'Given back', value: money(t.refundedCents) },
                      ]}
                    />
                  ),
                }
              : {}),
          }}
          search={{
            label: 'Find a customer, reference or invoice',
            value: typed,
            onChange: type,
            maxLength: 100,
          }}
          views={{
            label: 'Show',
            items: SHOWS,
            value: f.show,
            onChange: (show) => set({ show: show as PaymentShow }),
          }}
          filters={
            <>
              <TextField
                type="date"
                width="date"
                label="Came in from"
                value={f.from}
                onChange={(from) => set({ from })}
              />
              <TextField
                type="date"
                width="date"
                label="to"
                value={f.to}
                onChange={(to) => set({ to })}
                error={wrong ? WRONG_DAYS : null}
              />
            </>
          }
          list={{
            title: 'Payments',
            ...(t ? { count: t.count } : {}),
            what: 'the payments',
            status: listStatus(q.data, q.error),
            failedText: q.error ?? 'The payments didn’t load.',
            onRetry: () => void q.reload(),
            children: q.data ? (
              <DataTable
                columns={paymentColumns({
                  timeZone: timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
                  invoiceHrefOf: invoiceHref,
                })}
                rows={more.rows}
                hrefOf={(p) => paymentHref(p.id)}
                more={moreRows(more, q.data.totals.count)}
                empty="No payments here. Try another filter."
              />
            ) : null,
          }}
        />
      </main>
    </div>
  );
}
